import { useCallback, useState } from 'react';
import { latestBattery, type SensorHealthIssue } from '@irrigation/core';
import { api, type SensorSummary } from '../api.js';
import { getConfig } from '../config.js';
import {
  Banner,
  Button,
  Card,
  CardSkeleton,
  EmptyState,
  Field,
  Modal,
  Pill,
  TextInput,
  useToast,
} from '../components/ui.js';
import { revealRow, rowDomId, useAttentionTarget } from '../lib/attention.js';
import { errorMessage, fmtPct, relativeTime } from '../lib/format.js';
import { useLoad } from '../lib/useLoad.js';

export function Sensors() {
  const { data: sensors, loading, error, reload } = useLoad(api.listSensors);
  const [creating, setCreating] = useState(false);
  const [info, setInfo] = useState<SensorSummary | null>(null);
  const [deleting, setDeleting] = useState<SensorSummary | null>(null);
  const toast = useToast();

  // A stale probe or a low battery has no dialog to open — the row already
  // spells out what to do — so an attention link just puts the user on it.
  const onAttention = useCallback((id: string) => revealRow('sensors', id), []);
  useAttentionTarget('sensors', !!sensors, onAttention);

  if (loading) return <CardSkeleton lines={2} />;
  if (error) return <Banner tone="error">{error}</Banner>;

  const list = sensors ?? [];

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Soil moisture sensors</h1>
          <div className="sub">
            Meters push readings here; zones that reference a sensor plan from measurements instead of estimates.
          </div>
        </div>
        <Button onClick={() => setCreating(true)}>Add sensor</Button>
      </div>

      {list.length === 0 ? (
        <Card>
          <EmptyState
            icon="💧"
            title="No sensors yet"
            actions={<Button onClick={() => setCreating(true)}>Add sensor</Button>}
          >
            Optional: if you have a soil-moisture meter, add it here and the scheduler will water from real readings
            instead of estimates. It works fine without one — it models soil moisture from the weather. Adding a sensor
            gives you an API key and a URL to point the meter at.
          </EmptyState>
        </Card>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="list" role="list" aria-label="Sensors">
            {list.map((s) => (
              // `tabIndex`: focusable by an attention link, but not by Tab.
              <div className="run-row" role="listitem" key={s.id} id={rowDomId('sensors', s.id)} tabIndex={-1}>
                <div className="grow">
                  <div className="title">{s.name}</div>
                  <div className="meta">{sensorMeta(s)}</div>
                  <SensorHealth issues={s.health?.issues ?? []} />
                </div>
                <div className="actions">
                  <Button variant="ghost" size="sm" onClick={() => setInfo(s)}>
                    Setup info
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => setDeleting(s)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {creating && (
        <CreateSensor
          onClose={() => setCreating(false)}
          onCreated={(sensor) => {
            setCreating(false);
            setInfo(sensor);
            void reload();
          }}
        />
      )}

      {info && <SetupInfo sensor={info} onClose={() => setInfo(null)} />}

      {deleting && (
        <DeleteConfirm
          sensor={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={async () => {
            // Refresh before closing, so the row's Delete button is already gone
            // when Modal restores focus (see the same note in Zones.tsx).
            const name = deleting.name;
            await reload();
            setDeleting(null);
            toast(`${name} deleted.`);
          }}
        />
      )}
    </div>
  );
}

/**
 * The one-line summary under a sensor's name: latest soil reading plus the
 * latest battery level. The battery is carried separately from the reading, so
 * it survives readings that omit it — when it came from its own report rather
 * than riding along on this reading, it is stamped with its own age so an
 * elderly battery figure can't read as current.
 */
function sensorMeta(s: SensorSummary): string {
  const battery = latestBattery(s);
  const last = s.lastReading;
  if (!last) {
    const nothingYet = 'No readings yet — point your meter at the ingest URL.';
    return battery ? `Battery ${battery.batteryPct}% · ${relativeTime(battery.at)} · ${nothingYet}` : nothingYet;
  }
  const soil = `Soil ${fmtPct(last.moisturePct)} · ${relativeTime(last.at)}`;
  if (!battery) return soil;
  const age = battery.at === last.at ? '' : ` (${relativeTime(battery.at)})`;
  return `${soil} · battery ${battery.batteryPct}%${age}`;
}

/** Plain-language badge + hint for one detected sensor-health problem. */
function healthCopy(issue: SensorHealthIssue): { label: string; tone: 'warn' | 'bad'; hint: string } {
  switch (issue.kind) {
    case 'STALE':
      return {
        label: 'No recent readings',
        tone: 'warn',
        hint: 'No reading in over 24 hours — zones using this sensor fall back to the weather model until it reports again. Check the meter power and network.',
      };
    case 'FLATLINE':
      return {
        label: 'Flatlined',
        tone: 'warn',
        hint: `Readings have barely moved (${issue.rangePct.toFixed(1)} pp) over ${issue.hours} h — a live probe drifts down as soil dries, so it may be stuck, unplugged, or out of the soil.`,
      };
    case 'LOW_BATTERY':
      return {
        label: `Low battery ${issue.batteryPct}%`,
        tone: 'bad',
        hint: 'Replace or recharge the battery soon to avoid gaps in readings.',
      };
  }
}

/** Health badges + hints for a sensor row; renders nothing when healthy. */
function SensorHealth({ issues }: { issues: SensorHealthIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <div className="sensor-health">
      {issues.map((issue, i) => {
        const h = healthCopy(issue);
        return (
          <div className="hitem" key={i}>
            <Pill tone={h.tone}>{h.label}</Pill>
            <span className="hint">{h.hint}</span>
          </div>
        );
      })}
    </div>
  );
}

/** The curl example a device uses to push readings to the ingest endpoint. */
function ingestCurl(sensorId: string, apiKey: string): string {
  return (
    `curl -X POST ${getConfig().apiUrl}/ingest/${sensorId} \\\n` +
    `  -H 'x-api-key: ${apiKey}' \\\n` +
    `  -H 'Content-Type: application/json' \\\n` +
    `  -d '{"moisturePct": 23.4}'`
  );
}

const preStyle = {
  overflowX: 'auto' as const,
  background: 'var(--inset)',
  padding: '0.75rem',
  borderRadius: '8px',
};

function CreateSensor({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (sensor: SensorSummary) => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const create = async () => {
    if (!name.trim()) {
      setError('Give the sensor a name.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      onCreated(await api.createSensor(name.trim()));
    } catch (e) {
      setError(errorMessage(e, 'Failed to create sensor.'));
      setBusy(false);
    }
  };

  return (
    <Modal narrow title="Add sensor" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void create();
        }}
      >
        {error && <Banner tone="error">{error}</Banner>}
        {/* No autoFocus: Modal takes initial focus itself (see components/ui.tsx). */}
        <Field label="Name">
          {(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Create sensor
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function SetupInfo({ sensor, onClose }: { sensor: SensorSummary; onClose: () => void }) {
  return (
    <Modal title={`Sensor "${sensor.name}" setup`} onClose={onClose}>
      <div className="stack">
        <Field label="Sensor ID">
          {(id) => (
            <TextInput
              id={id}
              className="mono"
              readOnly
              value={sensor.id}
              onFocus={(e) => e.currentTarget.select()}
            />
          )}
        </Field>
        <Field label="API key">
          {(id) => (
            <TextInput
              id={id}
              className="mono"
              readOnly
              value={sensor.apiKey}
              onFocus={(e) => e.currentTarget.select()}
            />
          )}
        </Field>
        <pre className="small" style={preStyle}>
          {ingestCurl(sensor.id, sensor.apiKey)}
        </pre>
        <p className="muted small">
          Send readings as often as you like — the scheduler uses the freshest one (up to 24 h old) when planning each
          morning. Optional fields: at (ISO timestamp), batteryPct. If your meter reports battery on its own cadence,
          post <code>{'{"batteryPct": 87}'}</code> by itself — the latest battery level is kept separately from the
          latest soil reading.
        </p>
        <div className="actions">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DeleteConfirm({
  sensor,
  onClose,
  onDeleted,
}: {
  sensor: SensorSummary;
  onClose: () => void;
  onDeleted: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const doDelete = async () => {
    setBusy(true);
    setError('');
    try {
      await api.deleteSensor(sensor.id);
      await onDeleted();
    } catch (e) {
      setError(errorMessage(e, 'Failed to delete.'));
      setBusy(false);
    }
  };

  return (
    <Modal narrow title={`Delete "${sensor.name}"?`} onClose={onClose}>
      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        <p className="muted small">
          Readings stop being accepted immediately and any zones using this sensor go back to weather-based estimates.
        </p>
        <div className="actions">
          <Button variant="danger" busy={busy} onClick={() => void doDelete()}>
            Delete sensor
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
